import React, { useEffect, useState } from "react"
import { Button, Typography } from "@mui/material"
import { useNavigate, useLocation } from "react-router-dom"
import userService from "../../api/services/userService"
import cvskillService from "../../api/services/cvskillService"
import User from "../../api/models/user"
import { LocationState } from "../../api/models/cvskill"
import "./cv-skill.scss"

function Cvskillpage() {
	const [userData, setUserData] = useState<User | null>(null)
	const [loading, setLoading] = useState(true)
	const navigate = useNavigate()
	const location = useLocation()
	const state = location.state as LocationState | null
	const { editMode, cvSkillId, userId } = state || {}

	useEffect(() => {
		const fetchData = async () => {
			const storedUserId = localStorage.getItem("selectedUserId")
			if (!storedUserId) return

			try {
				setLoading(true)
				const user = await userService.getById(Number(storedUserId))
				setUserData(user)

				const existingCvSkill =
					await cvskillService.getCvSkillsByUserId(
						Number(storedUserId),
					)

				if (existingCvSkill) {
					navigate("/cvskill/cv", {
						state: {
							editMode: true,
							cvSkillId: existingCvSkill.id,
							userId: Number(storedUserId),
						},
					})
					return
				}

				if (editMode && cvSkillId) {
					const cvSkill =
						await cvskillService.getCvSkillById(cvSkillId)
					localStorage.setItem(
						`cvSkill-${cvSkillId}`,
						JSON.stringify(cvSkill),
					)
				}
			} catch (error) {
				// Gestion silencieuse de l'erreur
			} finally {
				setLoading(false)
			}
		}
		fetchData()
	}, [editMode, cvSkillId, navigate])

	const handleContinue = () => {
		navigate("/cvSkill/poleloisirsInteret", {
			state: {
				...state,
				userId: userData?.id || userId,
			},
		})
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center h-screen">
				<div className="text-center">
					<div
						className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 mx-auto mb-4"
						style={{ borderColor: "#2daf8e" }}
					/>
					<Typography style={{ color: "#2daf8e", fontWeight: 600 }}>
						Chargement des données utilisateur...
					</Typography>
				</div>
			</div>
		)
	}

	if (!userData) {
		return (
			<div className="flex items-center justify-center h-screen">
				<Typography style={{ color: "#ef4444", fontWeight: 600 }}>
					Aucune donnée utilisateur trouvée.
				</Typography>
			</div>
		)
	}

	const fields = [
		{ label: "Civilité", value: userData.civility, icon: "👤" },
		{ label: "Nom", value: userData.name, icon: "📝" },
		{ label: "Prénom", value: userData.firstName, icon: "✨" },
		{ label: "Téléphone", value: userData.phone, icon: "📱" },
		{ label: "Email", value: userData.email, icon: "📧" },
		{ label: "Date de naissance", value: userData.dateOfBirth, icon: "🎂" },
		{
			label: "Diplôme ou niveau d'études",
			value: userData.diploma,
			icon: "🎓",
		},
	]

	return (
		<div
			className="container mx-auto p-4 max-w-2xl relative pb-16"
			style={{
				height: "calc(100vh - 8rem)",
				overflowY: "auto",
				scrollbarWidth: "none",
				msOverflowStyle: "none",
			}}
		>
			<style>{`
                .container::-webkit-scrollbar {
                    display: none;
                }
                
                .info-card {
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 16px 20px;
                    margin-bottom: 16px;
                    transition: all 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                
                .info-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.15);
                    border-color: #2daf8e;
                }
                
                .info-icon {
                    font-size: 28px;
                    min-width: 40px;
                    text-align: center;
                    background: linear-gradient(135deg, #d4f5ed 0%, #b8ede0 100%);
                    padding: 8px;
                    border-radius: 10px;
                }
                
                .info-content {
                    flex: 1;
                }
                
                .info-label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #2daf8e;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 4px;
                }
                
                .info-value {
                    font-size: 16px;
                    font-weight: 500;
                    color: #374151;
                }
                
                .page-title {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%);
                    color: white;
                    padding: 16px 24px;
                    border-radius: 12px;
                    font-size: 24px;
                    font-weight: 700;
                    text-align: center;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3);
                    margin-bottom: 32px;
                }
                
                .continue-button {
                    background: linear-gradient(135deg, #2daf8e 0%, #238f74 100%) !important;
                    box-shadow: 0 4px 12px rgba(45, 175, 142, 0.3) !important;
                    text-transform: none !important;
                    font-size: 16px !important;
                    font-weight: 600 !important;
                    padding: 12px 24px !important;
                    border-radius: 10px !important;
                    transition: all 0.3s ease !important;
                }
                
                .continue-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(45, 175, 142, 0.4) !important;
                }
            `}</style>

			<div className="page-title">👤 Pôle Civilité</div>

			<div className="space-y-4">
				{fields.map((field, index) => (
					// eslint-disable-next-line react/no-array-index-key
					<div key={index} className="info-card">
						<div className="info-icon">{field.icon}</div>
						<div className="info-content">
							<div className="info-label">{field.label}</div>
							<div className="info-value">
								{field.value instanceof Date
									? field.value.toLocaleDateString()
									: field.value || "Non renseigné"}
							</div>
						</div>
					</div>
				))}

				<Button
					variant="contained"
					onClick={handleContinue}
					className="continue-button w-full mt-8"
				>
					Continuer →
				</Button>
			</div>
		</div>
	)
}

export default Cvskillpage
